import React, { useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTelemetryContext } from "@smb/web-telemetry";

import { validateGuid } from "../../utils/data-validation/validateGuid";
import { validateLaunchSource } from "../../utils/data-validation/validateLaunchSource";
import { updateLoggingContext } from "../telemetry/utils/updateLoggerContext";
import { ContextColumns } from "../telemetry/utils/constants";

import { setAuthSessionVariables } from "./useAuth";
import { AuthContext } from "./AuthContext";
import { useAuth } from "./useAuth";

export const AuthProvider: React.FC<React.PropsWithChildren<any>> = ({ children }) => {
  // Grab the url parameters and clear them if present upon initial launch
  const [searchParams, setSearchParams] = useSearchParams();
  const { sessionId, cid, serviceBaseUrl, source, tid } = useMemo(
    () => ({
      sessionId: searchParams.get("sessionId") ?? "",
      cid: searchParams.get("cid") ?? "",
      serviceBaseUrl: searchParams.get("serviceBaseUrl") ?? "",
      source: searchParams.get("source") ?? "",
      tid: searchParams.get("tid") ?? "",
    }),
    []
  );

  const { setTelemetryTenantId } = useTelemetryContext();
  useEffect(() => setTelemetryTenantId(tid), [tid, setTelemetryTenantId]);
  useEffect(() => setSearchParams([]), [setSearchParams]);

  setAuthSessionVariables({
    sessionId: sessionId,
    correlationId: cid,
    serviceBaseUrl: serviceBaseUrl,
    launchSource: source,
    tenantId: tid,
  });

  const auth = useAuth();

  // If the sessionId was present in the URL parameters, then update the session auth
  // NOTE: serviceBaseUrl is validated when creating the apollo client
  if (sessionId) {
    setAuthSessionVariables({
      sessionId: validateGuid(sessionId),
      correlationId: validateGuid(cid),
      serviceBaseUrl: serviceBaseUrl,
      launchSource: validateLaunchSource(source),
      tenantId: validateGuid(tid),
    });

    // Update the logging context to include valid sessionId and CorrelationId
    updateLoggingContext(undefined, {
      [ContextColumns.AuthSession]: source ? "Authorized" : "Anonymous",
      [ContextColumns.TenantId]: tid,
    });
  }
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => React.useContext(AuthContext);
